import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  ERROR_MESSAGE_WEATHER_CURRENT_CONDITIONS__204,
  ERROR_MESSAGE_WEATHER_MODULE__500,
} from '../../../../configurations/error-messages';
import { callEndpoint, telemetry } from '../../../../services/backend';
import { selectUnitsConfig } from '../../../../services/store/modules/app-controls';
import {
  formatWeatherForecastCurrentConditions,
  type WeatherCurrentConditionsApiResponse,
  type WeatherForecastCurrentConditionsViewModel,
  type WeatherSummaryApiResponse,
} from '../components/CurrentConditions/formatCurrentConditions';

export interface WeatherForecastCurrentConditionsError {
  status?: number;
  type?: 'warning';
  message: string;
}

const hasValidCoordinates = (
  latitude: number | undefined,
  longitude: number | undefined,
) =>
  latitude != null &&
  longitude != null &&
  Number.isFinite(latitude) &&
  Number.isFinite(longitude);

const isEmptyResponse = (data: unknown) =>
  data != null &&
  data.constructor === Object &&
  Object.entries(data as object).length === 0;

export const useWeatherForecastCurrentConditions = (
  latitude: number | undefined,
  longitude: number | undefined,
  enabled = true,
) => {
  const unitsConfig = useSelector(selectUnitsConfig);
  const [data, setData] =
    useState<WeatherForecastCurrentConditionsViewModel | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] =
    useState<WeatherForecastCurrentConditionsError | null>(null);

  useEffect(() => {
    let isMounted = true;

    if (!enabled || !hasValidCoordinates(latitude, longitude)) {
      setData(null);
      setError(null);
      setIsLoading(false);
      return () => {
        isMounted = false;
      };
    }

    const loadCurrentConditions = async () => {
      setIsLoading(true);
      setError(null);
      setData(null);

      try {
        const queryParams = {
          latitude: latitude as number,
          longitude: longitude as number,
        };

        const [weatherSummaryData, currentConditionsData] = await Promise.all([
          callEndpoint(
            callEndpoint.endpoints.queryWeatherSummary,
            {},
            queryParams,
          ) as Promise<WeatherSummaryApiResponse>,
          callEndpoint(
            callEndpoint.endpoints.queryWeatherCurrentConditions,
            {},
            queryParams,
          ) as Promise<WeatherCurrentConditionsApiResponse>,
        ]);

        if (!isMounted) {
          return;
        }

        if (
          isEmptyResponse(weatherSummaryData) &&
          isEmptyResponse(currentConditionsData)
        ) {
          setError({
            status: 204,
            type: 'warning',
            message: ERROR_MESSAGE_WEATHER_CURRENT_CONDITIONS__204,
          });
          return;
        }

        setData(
          formatWeatherForecastCurrentConditions(
            isEmptyResponse(weatherSummaryData) ? null : weatherSummaryData,
            isEmptyResponse(currentConditionsData)
              ? null
              : currentConditionsData,
            unitsConfig,
          ),
        );
      } catch (loadError) {
        telemetry(
          telemetry.ERROR,
          `[useWeatherForecastCurrentConditions.loadCurrentConditions] ${JSON.stringify(loadError)}`,
        );

        if (isMounted) {
          setError({
            message: ERROR_MESSAGE_WEATHER_MODULE__500,
          });
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadCurrentConditions();

    return () => {
      isMounted = false;
    };
  }, [enabled, latitude, longitude, unitsConfig]);

  return {
    data,
    isLoading,
    error,
  };
};
